import { Suspense, useState, useEffect } from 'react'
import { Toaster } from "@/components/ui/toaster"
import { Toaster as SonnerToaster } from "@/components/ui/sonner"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VerifyEmail from './pages/VerifyEmail';
import WelcomeModal from '@/components/onboarding/WelcomeModal';
import TrialExpiredPaywall from '@/components/TrialExpiredPaywall';
import { api } from '@/api/client';
import { SimulatedChannelsBanner } from '@/components/dev/SimulatedChannelsBanner.jsx';

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
const MainPage = mainPageKey ? Pages[mainPageKey] : <></>;

const LayoutWrapper = ({ children, currentPageName }) => Layout ?
  <Layout currentPageName={currentPageName}>{children}</Layout>
  : <>{children}</>;

// Onboarding redirect guard — checks if user needs to complete onboarding
// Step-to-route mapping: some onboarding steps use non-/onboarding/ pages
const ONBOARDING_STEP_ROUTES = {
  'about-you':       '/onboarding/about-you',
  'create-workspace':'/onboarding/create-workspace',
  'connect-account': '/onboarding/connect-account',
  'complete':        '/onboarding/complete',
  'agency-setup':    '/onboarding/agency-setup',
  'invite-pm':       '/onboarding/invite-pm',
  'admin-complete':  '/onboarding/admin-complete',
  'create-account':  '/onboarding/create-account',
  'role-context':    '/onboarding/role-context',
  'custom-needs':    '/onboarding/custom-needs',
  'custom-complete': '/onboarding/custom-complete',
  'wait-for-channels': '/onboarding/wait-for-channels',
  // Legacy routes kept for backwards compat
  'greet':           '/onboarding/greet',
  'profile':         '/onboarding/create-workspace',
  'link-sync':       '/onboarding/connect-account',
  'agency':          '/onboarding/agency-setup',
  'role-selection':  '/onboarding/about-you',
  'billing':         '/onboarding/complete',
  'credits':         '/onboarding/complete',
  'allocate':        '/onboarding/complete',
};

// Pages that are allowed during onboarding (don't redirect away from these)
// ANALYTICS & AI FEATURES (Dashboard, Reports, TargetPacing, ChatAssistant,
// Skills, Audits) are intentionally absent — locked until profile created.
const ONBOARDING_ALLOWED_PATHS = [
  '/verify-email',
  '/accept-invite/',
  '/invitation/accept/',
  '/onboarding/',
  '/Billing',
  '/billing/',
  '/Settings',
];

const OnboardingRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const isAllowedPage = ONBOARDING_ALLOWED_PATHS.some(p =>
      location.pathname.startsWith(p)
    );
    if (isAllowedPage) return;

    api.onboarding.getStatus()
      .then((res) => {
        const status = res.data;

        try {
          const raw = localStorage.getItem('user');
          const u = raw ? JSON.parse(raw) : null;
          if (u && u.email_verified === false) {
            navigate('/verify-email', { replace: true });
            return;
          }
        } catch {
          /* ignore */
        }

        if (
          status &&
          !status.onboarding_complete &&
          !status.skipped &&
          status.next_step
        ) {
          const route =
            ONBOARDING_STEP_ROUTES[status.next_step] ||
            `/onboarding/${status.next_step}`;
          navigate(route, { replace: true });
        }
      })
      .catch(() => {});
  }, [location.pathname, navigate]);

  return null;
};

const PAYWALL_EXEMPT = ['/Billing', '/billing/', '/Settings', '/logout'];

const AuthenticatedApp = () => {
  const { isLoadingAuth, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showWelcome, setShowWelcome] = useState(false);
  const [trialExpired, setTrialExpired] = useState(false);
  const [deletionDate, setDeletionDate] = useState(null);
  const [paywallReason, setPaywallReason] = useState('trial');

  const isPaywallExempt = PAYWALL_EXEMPT.some((p) =>
    location.pathname.startsWith(p),
  );

  useEffect(() => {
    const onTrialExpired = (e) => {
      setTrialExpired(true);
      setDeletionDate(e.detail?.deletionDate || null);
      setPaywallReason('trial');
    };
    window.addEventListener('trial-expired', onTrialExpired);
    return () => window.removeEventListener('trial-expired', onTrialExpired);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;

    (async () => {
      try {
        const raw = localStorage.getItem('user');
        if (!raw) return;
        const u = JSON.parse(raw);
        if (u.trial_ends_at) {
          const trialEnd = new Date(u.trial_ends_at);
          if (trialEnd < new Date()) {
            if (!cancelled) {
              setTrialExpired(true);
              setPaywallReason('trial');
            }
            return;
          }
        }
      } catch {
        /* ignore */
      }

      try {
        const { data: credits } = await api.credits.balance();
        const total =
          (credits?.subscription_credits_available || 0) +
          (credits?.purchased_credits_available || 0);

        if (total === 0) {
          const { data: sub } = await api.subscriptions.getStatus();
          if (sub?.status !== 'active') {
            if (!cancelled) {
              setTrialExpired(true);
              setPaywallReason('credits');
              setDeletionDate(null);
            }
          }
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const shown = localStorage.getItem('agent_david_welcome_shown');
    if (!shown) setShowWelcome(true);
  }, [isAuthenticated]);

  // Show loading spinner while checking auth
  if (isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-ad-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  // Not authenticated — redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Render the main app — Suspense handles lazy-loaded page chunks
  return (
    <Suspense fallback={
      <div className="fixed inset-0 flex items-center justify-center bg-ad-bg">
        <div className="w-8 h-8 border-4 border-ad-border border-t-ad-primary rounded-full animate-spin"></div>
      </div>
    }>
      {trialExpired && !isPaywallExempt && (
        <TrialExpiredPaywall deletionDate={deletionDate} reason={paywallReason} />
      )}
      <WelcomeModal
        open={showWelcome}
        onComplete={() => {
          setShowWelcome(false);
          navigate('/onboarding/about-you');
        }}
      />
      <OnboardingRedirect />
      <Routes>
        <Route path="/" element={
          <LayoutWrapper currentPageName={mainPageKey}>
            <MainPage />
          </LayoutWrapper>
        } />
        <Route path="/ReportBuilder" element={<Navigate to="/Reports" replace />} />
        <Route path="/Credits" element={<Navigate to="/Billing?tab=credits" replace />} />
        <Route path="/AutoRecharge" element={<Navigate to="/Billing?tab=auto-recharge" replace />} />
        <Route path="/AdminCredits" element={<Navigate to="/Billing?tab=manage-credits" replace />} />
        <Route path="/Pricing" element={<Navigate to="/Billing?tab=pricing" replace />} />
        <Route path="/TeamManagement" element={<Navigate to="/Team?tab=roles-permissions" replace />} />
        <Route path="/Invitations" element={<Navigate to="/Team?tab=invitations" replace />} />
        <Route path="/RoleAuditLog" element={<Navigate to="/Team?tab=roles-permissions" replace />} />
        {Object.entries(Pages).map(([path, Page]) => (
          <Route
            key={path}
            path={`/${path}`}
            element={
              <LayoutWrapper currentPageName={path}>
                <Page />
              </LayoutWrapper>
            }
          />
        ))}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
};


function App() {

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <NavigationTracker />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/*" element={<AuthenticatedApp />} />
          </Routes>
        </Router>
        <Toaster />
        <SonnerToaster />
        <SimulatedChannelsBanner />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App
